<script lang="ts">
	import { get } from 'svelte/store';
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	// Design system components
	import { Alert } from '@Shelly/shelly-design-system/index';
	// Helpers
	import { isTestUser } from 'src/helpers/user_helpers';
	import {
		NormalizeFactorFilter,
		PressureTrendFilter,
		SkyCondition,
		type PressureTrend,
		type SkyConditions,
		type SkyConditionsInfo,
	} from './weather_station_cloud_helpers';
	import { feelsLike } from './feels_like';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { save_home } from 'src/helpers/home_helpers';
	import { home_data } from 'src/stores/account/user';
	import {
		CACHE_WS_STATISTICS_TIMEOUT,
		getLast24WSStatistics,
	} from './weather_station_statistics_helper';
	import { dateFormat } from 'src/helpers/date_helper';
	import { convertElevation } from 'src/helpers/units/elevation_units';
	// Cards
	import CardGroup from './Cards/CardGroup.svelte';
	import TemperatureCard from './Cards/TemperatureCard.svelte';
	import SkyCard from './Cards/SkyCard.svelte';
	import WindCard from './Cards/WindCard.svelte';
	import RainCard from './Cards/RainCard.svelte';
	import UvCard from './Cards/UvCard.svelte';
	import PressureCard from './Cards/PressureCard.svelte';
	import HumidityCard from './Cards/HumidityCard.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let device: Device,
		statuses = [],
		usedInSidepanel = false;

	let waitForInitResponse = false;
	let locationSettings: Device['location'] = device?.location;

	$: temp = statuses.find((i) => i.name === 'temperature:0');
	$: rh = statuses.find((i) => i.name === 'humidity:0')?.rawValue;
	$: windMs = statuses.find((i) => i.name === 'speed:0')?.rawValue;

	let minTemp24h: string = '--';
	let maxTemp24h: string = '--';
	let elevation: number = 0;
	let skyConditionLabel: SkyConditions | '' = '';
	let pressureTrendLabel: PressureTrend = 'STEADY';
	let skyInfo: SkyConditionsInfo;
	let interval: NodeJS.Timeout;

	const getWSStatistics = async () => {
		const skyCondition: SkyCondition = locationSettings
			? new SkyCondition(locationSettings.lat, locationSettings.lng, locationSettings.tz)
			: null;
		const lux = statuses.find((i) => i.name === 'illuminance:0')?.rawValue;
		const uv = statuses.find((i) => i.name === 'UV:0')?.rawValue;
		const pressure = statuses.find((i) => i.name === 'pressure:0')?.rawValue;
		const currentTemp = temp?.value;
		const last24Temperature = await getLast24WSStatistics(device, 'temperature', true);
		const historyData = [
			...(last24Temperature?.resData?.history || []),
			{
				datetime: dateFormat(new Date(), 'Y-m-dTH:i:sZ', true),
				uv,
				illuminance: lux,
				min_pressure: pressure,
				max_pressure: pressure,
			},
		];

		skyInfo = skyCondition ? skyCondition.getSkyInfo(lux, uv) : null;
		elevation = Number(locationSettings?.ele || 0) + Number(locationSettings?.ele_offset || 0);
		minTemp24h = last24Temperature?.chartStatistic?.min || currentTemp || '--';
		maxTemp24h = last24Temperature?.chartStatistic?.max || currentTemp || '--';

		if (parseFloat(minTemp24h) > temp?.rawValue) {
			minTemp24h = currentTemp;
		}
		if (parseFloat(maxTemp24h) < temp?.rawValue) {
			maxTemp24h = currentTemp;
		}
		if (
			skyInfo &&
			(skyInfo.sky === SkyCondition.NIGHT ||
				skyInfo.sky === SkyCondition.DAWN ||
				skyInfo.sky === SkyCondition.DUSK)
		) {
			skyConditionLabel = skyInfo.sky;
		} else if (skyCondition) {
			const nSkyCondition = new NormalizeFactorFilter(skyCondition);
			const last2HoursHistoryData: GetStatsResponseHistoryData[] = historyData.slice(-3);
			skyConditionLabel = nSkyCondition.getCloudStatus(last2HoursHistoryData)?.cond;
		}

		const pressureTrend = new PressureTrendFilter();
		const last3HoursHistoryData: GetStatsResponseHistoryData[] = historyData.slice(-4);
		pressureTrendLabel = pressureTrend.getPressureTrend(last3HoursHistoryData);
		interval = setTimeout(getWSStatistics, CACHE_WS_STATISTICS_TIMEOUT);

		if (isTestUser()) {
			console.log('Weather station:', {
				statuses,
				locationSettings,
				feelsLike: feelsLike(temp?.rawValue, rh, windMs, temp?.unit),
				pressureTrendLabel,
			});
			if (skyCondition) {
				console.log('skyCondition:', skyCondition.getSkyInfo(lux, uv));
			}
		}
	};

	const loadStatistics = async () => {
		waitForInitResponse = true;
		await getWSStatistics();
		waitForInitResponse = false;
	};

	onMount(() => {
		loadStatistics();
		return () => {
			clearTimeout(interval);
		};
	});

	const updateHomeData = () => {
		const dateNow = new Date();
		save_home('customDeviceCardInvite', {
			seen: true,
			date: dateNow,
		});
		home_data.set({
			...$home_data,
			customDeviceCardInvite: {
				seen: true,
				date: dateNow,
			},
		});
	};

	const gotoDeviceMap = () => {
		navigateToSetting(device.id, 'geo_location_and_elevation', '', false);
	};

	const dispatch = createEventDispatcher();

	const handleCardClick = (chartKeywords) => {
		dispatch('openChart', chartKeywords);
	};
</script>

<div class={classMap({ 'main-tab-container': usedInSidepanel })}>
	{#if !get(home_data)?.customDeviceCardInvite?.seen}
		<Alert
			isHeadingBold
			class="mb-s"
			heading={$format('alert_customizable_device_card_heading', {
				default: 'Customizable Device Card',
			})}
			message={$format('alert_customizable_device_card_message', {
				default: 'Select what you see on the device card for quick, at-a-glance insights.',
			})}
			cta={{
				title: $format('alert_customize_card_link', {
					default: 'Customize Card',
				}),
			}}
			dismissable
			on:dismissable_click={updateHomeData}
			on:cta_click={() => {
				navigateToSetting(device.id, 'application_settings', 'device_card_customization');
				updateHomeData();
			}}
		/>
	{/if}

	<CardGroup>
		<TemperatureCard
			on:click={() => handleCardClick('temperature')}
			slot="left"
			{temp}
			{minTemp24h}
			{maxTemp24h}
			feelsLike={feelsLike(temp?.rawValue, rh, windMs, temp?.unit)}
		/>
		<SkyCard
			slot="right"
			{skyInfo}
			{skyConditionLabel}
			{device}
			loading={waitForInitResponse}
			setLocationAlert={!locationSettings}
			disableSetLocationAlert={device?.shared}
			on:click={gotoDeviceMap}
		/>
	</CardGroup>
	<WindCard {statuses} />
	<CardGroup>
		<RainCard slot="left" {statuses} on:click={() => handleCardClick('precipitation')} />
		<UvCard slot="right" {statuses} on:click={() => handleCardClick('uv')} />
	</CardGroup>
	<CardGroup>
		<PressureCard
			slot="left"
			{statuses}
			elevation={convertElevation(elevation, device?.ht_ui?.elevation_unit || 'm')}
			{pressureTrendLabel}
			setLocationAlert={!locationSettings}
			disableSetLocationAlert={device?.shared}
			on:click={() => {
				locationSettings ? handleCardClick('pressure') : gotoDeviceMap();
			}}
		/>
		<HumidityCard slot="right" {statuses} on:click={() => handleCardClick('humidity')} />
	</CardGroup>
</div>

<style>
	.main-tab-container {
		background-color: #0e0f10;
		padding: 8px 8px 4px 8px;
		border-radius: 18px;
		margin-bottom: 8px;
	}
</style>
